'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import Icon from '@/components/ui/AppIcon';

interface LoginFields {
  username: string;
  password: string;
}

const VALID_USERNAME = 'M01101101';
const VALID_PASSWORD = 'PinkSponge1!';

export default function LoginForm() {
  const router = useRouter();
  const [showPass, setShowPass] = useState(false);
  const [authError, setAuthError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFields>();

  const onSubmit = async (data: LoginFields) => {
    setAuthError('');
    setIsLoading(true);
    // BACKEND INTEGRATION: Replace with real auth endpoint
    await new Promise((r) => setTimeout(r, 900));
    if (data.username === VALID_USERNAME && data.password === VALID_PASSWORD) {
      router.push('/chat-interface');
    } else {
      setIsLoading(false);
      setAuthError('Invalid credentials. Check username and password.');
    }
  };

  return (
    <div className="w-full max-w-sm relative z-10">
      {/* Card */}
      <div className="card-surface p-8 shadow-2xl" style={{ boxShadow: '0 25px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(124,58,237,0.1)' }}>
        {/* Brand */}
        <div className="flex flex-col items-center mb-8">
          <div className="flex items-center gap-3 mb-3">
            <AppLogo size={36} />
            <span className="text-xl font-semibold text-foreground">DevSandbox</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground font-mono">v2.4.1</span>
            <span className="w-1 h-1 rounded-full bg-muted-foreground" />
            <span className="text-xs text-muted-foreground">Personal AI Sandbox</span>
          </div>
        </div>

        {/* Model indicators */}
        <div className="flex items-center justify-center gap-1.5 mb-7">
          {['bg-gemini', 'bg-openai', 'bg-grok', 'bg-claude', 'bg-perplexity'].map((color, i) => (
            <div key={`model-dot-${i}`} className={`w-2 h-2 rounded-full ${color} opacity-70`} />
          ))}
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
          {/* Username */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5 tracking-wide uppercase">
              Username
            </label>
            <input
              {...register('username', { required: 'Username is required' })}
              type="text"
              autoComplete="username"
              placeholder="M01101101"
              className="input-field font-mono text-foreground placeholder:text-muted-foreground/50"
              spellCheck={false}
            />
            {errors.username && (
              <p className="mt-1 text-xs text-danger">{errors.username.message}</p>
            )}
          </div>

          {/* Password */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1.5 tracking-wide uppercase">
              Password
            </label>
            <div className="relative">
              <input
                {...register('password', { required: 'Password is required' })}
                type={showPass ? 'text' : 'password'}
                autoComplete="current-password"
                placeholder="••••••••••••"
                className="input-field pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPass((p) => !p)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showPass ? 'Hide password' : 'Show password'}
              >
                <Icon name={showPass ? 'EyeSlashIcon' : 'EyeIcon'} size={16} />
              </button>
            </div>
            {errors.password && (
              <p className="mt-1 text-xs text-danger">{errors.password.message}</p>
            )}
          </div>

          {/* Auth error */}
          {authError && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-danger/10 border border-danger/20">
              <Icon name="ExclamationCircleIcon" size={14} className="text-danger flex-shrink-0" />
              <p className="text-xs text-danger">{authError}</p>
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={isLoading}
            className="btn-primary w-full justify-center py-2.5 mt-2"
            style={{ minHeight: 40 }}
          >
            {isLoading ? (
              <>
                <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
                <span>Authenticating…</span>
              </>
            ) : (
              <>
                <Icon name="ArrowRightOnRectangleIcon" size={16} />
                <span>Sign In</span>
              </>
            )}
          </button>
        </form>

        {/* Footer note */}
        <p className="text-center text-xs text-muted-foreground mt-6">
          Single-user instance · All sessions local
        </p>
      </div>

      {/* Bottom status bar */}
      <div className="mt-4 flex items-center justify-center gap-4">
        <div className="flex items-center gap-1.5">
          <div className="w-1.5 h-1.5 rounded-full bg-success animate-pulse" />
          <span className="text-xs text-muted-foreground font-mono">5 models ready</span>
        </div>
        <span className="text-muted-foreground text-xs">·</span>
        <span className="text-xs text-muted-foreground font-mono">2026-06-08</span>
      </div>
    </div>
  );
}